// import components
import { Tooltip, Typography } from '@mui/material';
import { showErrorSnackbar } from 'components/shared/Snackbar';
import PlanUnitSelector from 'components/shared/PlanUnitSelector';
import {
  StyledAiBasicIntroBox,
  StyledLeftContentBox,
  StyledLink
} from '../AIMatch.styles';

// import hooks
import { Trans, useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useAIMatchCrudPlanningEntity } from '../hooks/useAIMatchCrudPlanningEntity';

// import types
import { AIMatchBasicTopBarProps } from './AIMatchBasicTopBar.types';
import { Icon } from '@sdb/planning-ui';

const AIMatchBasicTopBar = ({
  hasDutyAutoAskEmployeesBasic,
  hasDutyAutoAskEmployeesAdvanced,
  setPendingCreations,
  setPendingDeletions,
  onPlanUnitSelected,
  setPlanUnits,
  defaultSelectedPlanUnits,
  pendingCreations,
  pendingDeletions,
  selectedPlanUnits,
  setSelectedPlanUnits
}: AIMatchBasicTopBarProps) => {
  const [maxLimitReached, setMaxLimitReached] = useState(false);
  const { t } = useTranslation('common', { keyPrefix: 'aiMatchSettings' });
  const { createPlanningEntity, deletePlanningEntity } =
    useAIMatchCrudPlanningEntity();

  const sender = encodeURIComponent(
    'ccplanning@sdbgroep.nl;binnendienst@sdbgroep.nl'
  );
  const subject = encodeURIComponent(t('aiMatchAdvancedRequestEmailSubject'));
  const body = encodeURIComponent(t('aiMatchAdvancedRequestEmailBody'));
  const mailToContent = `mailto:${sender}?subject=${subject}&body=${body}`;

  // handle pending creations
  useEffect(() => {
    if (pendingCreations.length === 0) return;
    const toCreate = [...pendingCreations];
    setPendingCreations([]);

    toCreate.forEach((entityId) => {
      createPlanningEntity(entityId, (error) => {
        if (error?.notifications) {
          showErrorSnackbar(t('maximumNumberOfOrganizationalUnitsExceeded'));
          setMaxLimitReached(true);

          setSelectedPlanUnits((prevUnits) =>
            prevUnits.filter((unit) => unit.entityId !== entityId)
          );
        }
      });
    });
  }, [pendingCreations, createPlanningEntity]);

  // handle pending deletions
  useEffect(() => {
    if (pendingDeletions.length === 0) return;

    const toDelete = [...pendingDeletions];
    setPendingDeletions([]);

    toDelete.forEach((entityId) => {
      deletePlanningEntity(entityId, (error) => {
        setMaxLimitReached(false);
        if (error) {
          showErrorSnackbar();
        }
      });
    });
  }, [pendingDeletions, deletePlanningEntity]);

  return (
    <>
      {hasDutyAutoAskEmployeesBasic && !hasDutyAutoAskEmployeesAdvanced && (
        <StyledAiBasicIntroBox>
          <StyledLeftContentBox>
            <Icon type="circle-info" color="inherit" />
            <Typography variant="body2">
              <Trans i18nKey="aiMatchSettings.aiMatchBasicIntroduction" />
            </Typography>
          </StyledLeftContentBox>

          <Tooltip title={t('sendEmail')}>
            <StyledLink href={mailToContent}>
              {t('orderAiMatchAdvanced')}
              <Icon type="lock-open" color="inherit" />
            </StyledLink>
          </Tooltip>
        </StyledAiBasicIntroBox>
      )}
      {hasDutyAutoAskEmployeesBasic && !hasDutyAutoAskEmployeesAdvanced && (
        <>
          <PlanUnitSelector
            onSelected={onPlanUnitSelected}
            setPlanUnits={setPlanUnits}
            defaultValue={defaultSelectedPlanUnits}
            selectedValues={selectedPlanUnits}
            doNotPersist
            variant="outlined"
            fullWidth
            hideNavigation
            hideGroups
            showChips
            helperText={
              maxLimitReached
                ? t('maximumNumberOfOrganizationalUnitsExceeded')
                : ''
            }
            disabled={maxLimitReached}
          />
        </>
      )}
    </>
  );
};

export default AIMatchBasicTopBar;
